/* eslint-disable jsx-a11y/anchor-is-valid */
import dynamic from 'next/dynamic'
import { useRef } from 'react'
import { useTranslations } from 'next-intl'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { Box, Link } from '@alto-avios/alto-ui'

import { usePlatformTheme } from '~/context/theme'
import { usePlatformRoutes } from '~/context/platform'

import { useTagManagerContext } from '~/context/analytics'
import { isClient } from '~/utils/envChecks'
import { HeaderProps } from './Header.types'
import {
  handleOpcoSwitcherClickCapture,
  handleOpcoSwitcherSubmitCapture,
  updateOpcoQueryParam,
} from './opcoSwitcherUrl'

import styles from './Header.module.scss'

const SharedHeader = dynamic(
  () => import('@channel/shared-header-footer').then((module) => module.Header),
  { ssr: false }
)

const WhitelabelHeader = dynamic(
  () => import('./WhitelabelHeader').then((module) => module.WhitelabelHeader),
  { ssr: false }
)

const GA_NAV_ELEMENT = 'header navigation'

const ORDERS_ROUTE_PREFIX = '/orders'
const ORDER_HISTORY_ROUTE = '/order-history'

const Header = ({
  userDisplayName = '',
  membershipNumber = '',
  aviosBalance = 0,
  basketItemsCount,
  isLoading,
  skipContentSelector,
}: HeaderProps) => {
  const router = useRouter()
  const { createRoutePath } = usePlatformRoutes()
  const { isAviosTheme } = usePlatformTheme()
  const t = useTranslations('components.header')
  const { pushEventToDataLayer } = useTagManagerContext()
  const pendingOpcoRef = useRef<string | undefined>(undefined)

  const showHomeLink =
    router.asPath.includes(ORDERS_ROUTE_PREFIX) || router.asPath.includes(ORDER_HISTORY_ROUTE)

  const updateCurrentUrlOpco = (opco: string) => {
    if (isClient()) {
      const nextUrl = updateOpcoQueryParam(window.location.href, opco)
      window.history.replaceState(window.history.state, '', nextUrl)
    }
  }

  const captureOpcoSwitcherClick = (event: React.MouseEvent<HTMLElement>) =>
    handleOpcoSwitcherClickCapture(event.target, pendingOpcoRef, updateCurrentUrlOpco)
  const captureOpcoSwitcherSubmit = (event: React.FormEvent<HTMLElement>) =>
    handleOpcoSwitcherSubmitCapture(event.target, pendingOpcoRef, updateCurrentUrlOpco)

  if (!isAviosTheme) {
    return (
      <WhitelabelHeader
        userDisplayName={userDisplayName}
        membershipNumber={membershipNumber}
        aviosBalance={aviosBalance}
        basketItemsCount={basketItemsCount}
        isLoading={isLoading}
      />
    )
  }

  return (
    <nav
      className={styles.headerContainer}
      onClickCapture={captureOpcoSwitcherClick}
      onSubmitCapture={captureOpcoSwitcherSubmit}
    >
      {/* The shared header sources auth state, user details and balances from AviosAuthProvider */}
      <SharedHeader skipTo={`#${skipContentSelector}`} />

      <div className={styles.subHeaderContainer}>
        <Box display="flex" gap="sm" align="center" justifyContent="flex-start">
          {showHomeLink ? (
            <NextLink href={createRoutePath('/')} passHref legacyBehavior>
              <Link
                underline={false}
                iconEndProps={false}
                iconStartProps={{ iconName: 'arrow-left' }}
                onPress={() => {
                  pushEventToDataLayer('navigation', {
                    nav_element: GA_NAV_ELEMENT,
                    nav_type: 'cta',
                    nav_action: `header home link`,
                    menu_level: `0`,
                  })
                }}
              >
                {t('actions.home')}
              </Link>
            </NextLink>
          ) : null}
        </Box>
        <Box display="flex" gap="sm" align="center" justifyContent="flex-end">
          <NextLink href={createRoutePath(ORDER_HISTORY_ROUTE)} passHref>
            <Link
              underline={false}
              iconEndProps={{ iconName: 'clock-rotate-left' }}
              onPress={() => {
                pushEventToDataLayer('navigation', {
                  nav_element: GA_NAV_ELEMENT,
                  nav_type: 'cta',
                  nav_action: `header order history`,
                  menu_level: `1`,
                })
              }}
            >
              {t('actions.order_history')}
            </Link>
          </NextLink>

          <NextLink
            href={createRoutePath(`${ORDERS_ROUTE_PREFIX}/payment-options`)}
            passHref
            legacyBehavior
          >
            <Link
              underline={false}
              iconEndProps={{
                iconName: 'cart-shopping',
                iconPrefix: basketItemsCount ? 'fas' : 'far',
              }}
              onPress={() => {
                pushEventToDataLayer('navigation', {
                  nav_element: GA_NAV_ELEMENT,
                  nav_type: 'cta',
                  nav_action: `header payment options`,
                  menu_level: `2`,
                })
              }}
            >
              {t('actions.basket')} {basketItemsCount ? ` (${basketItemsCount})` : ''}
            </Link>
          </NextLink>
        </Box>
      </div>
    </nav>
  )
}

export default Header
